import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  getClaims,
  type ClaimsSummary,
  getClaimsOverview,
} from '@services/api/dashboard/finance.api'
import {
  computeChartAxis,
  formatCurrency,
  formatPercent,
} from '@utils/dashboardFinance.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'

export type UseClaimsOptions = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

export type ClaimsActivityRow = {
  period: string
  underReview: string
  paid: string
  pending: string
  denied: string
  paidRate: string
}

export type ClaimsChartRow = {
  label: string
  value: number
}

export type UseClaimsResult = {
  loading: boolean
  error: string | null
  /** Response has arrived (`data != null`); independent of nonzero content. */
  hasPayload: boolean
  hasData: boolean
  summary: ClaimsSummary | undefined
  underReviewStr: string
  paidStr: string
  pendingStr: string
  deniedStr: string
  chart: {
    data: ClaimsChartRow[]
    yDomain: [number, number]
    yTicks: number[]
  }
  activityRows: ClaimsActivityRow[]
}

function useClaimsFromFetcher({
  range,
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseClaimsOptions & {
  fetcher: DashboardPeriodFetcher<Awaited<ReturnType<typeof getClaims>>>
}): UseClaimsResult {
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    queryParams,
    fetcher,
    { enabled, queued },
  )

  const summary = data?.summary

  const underReviewStr = useMemo(
    () => formatCurrency(summary?.underReview.current ?? 0, { compact: true }),
    [summary],
  )

  const paidStr = useMemo(
    () => formatCurrency(summary?.paid.current ?? 0, { compact: true }),
    [summary],
  )

  const pendingStr = useMemo(
    () => formatCurrency(summary?.pending.current ?? 0, { compact: true }),
    [summary],
  )

  // TODO: denied is hardcoded to 0 on backend until denied source is defined
  const deniedStr = useMemo(
    () => formatCurrency(summary?.denied.current ?? 0, { compact: true }),
    [summary],
  )

  const chart = useMemo(() => {
    const underReview = summary?.underReview.current ?? 0
    const paid = summary?.paid.current ?? 0
    const pending = summary?.pending.current ?? 0
    const denied = summary?.denied.current ?? 0

    const chartData: ClaimsChartRow[] = [
      { label: 'Under review', value: underReview / 1000 },
      { label: 'Paid', value: paid / 1000 },
      { label: 'Pending', value: pending / 1000 },
      { label: 'Denied', value: denied / 1000 },
    ]

    const axis = computeChartAxis(chartData.map((d) => d.value))

    return {
      data: chartData,
      yDomain: axis.yDomain,
      yTicks: axis.yTicks,
    }
  }, [summary])

  const activityRows = useMemo((): ClaimsActivityRow[] => {
    return (data?.byPeriod ?? []).map((row) => ({
      period: row.period,
      underReview: formatCurrency(row.underReview, { compact: true }),
      paid: formatCurrency(row.paid, { compact: true }),
      pending: formatCurrency(row.pending, { compact: true }),
      denied: formatCurrency(row.denied, { compact: true }),
      paidRate: formatPercent(row.paidOverSubmittedPercent),
    }))
  }, [data])

  return {
    loading,
    error,
    hasPayload: data != null,
    hasData: hasNonZeroDashboardValues(
      summary?.underReview.current,
      summary?.pending.current,
      summary?.paid.current,
      summary?.denied.current,
    ),
    summary,
    underReviewStr,
    paidStr,
    pendingStr,
    deniedStr,
    chart,
    activityRows,
  }
}

export function useClaims(options: UseClaimsOptions): UseClaimsResult {
  return useClaimsFromFetcher({ ...options, fetcher: getClaims })
}

export function useClaimsOverview(options: UseClaimsOptions): UseClaimsResult {
  return useClaimsFromFetcher({ ...options, fetcher: getClaimsOverview })
}
